import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../../../lib/api';
import { showToast } from '../../../../components/Toast';
import { formatQuantity } from '../../../../utils/formatters';

// ── Tipi condivisi ───────────────────────────────────────────────────────────

export interface CandidatoDdt {
  rigaOrdineId: number | null;
  rigaOrdinePos: number | null;
  componenteId: number | null;
  rigaDistintaId: number | null;
  articoloId: number | null;
  codice: string | null;
  descrizione: string;
  unitaMisura: string | null;
  quantita: number | null;
  giaConsegnata: number;
  residuo: number | null;
}

interface Props {
  tipo: 'VENDITA' | 'CANTIERE';
  ordineId?: number; // per tipo VENDITA
  distintaId?: number; // per tipo CANTIERE
  onClose: () => void;
  onCreated: (ddtId: number) => void;
}

function estraiErrore(e: unknown, fallback: string): string {
  if (e && typeof e === 'object') {
    const payload = (e as { payload?: { error?: unknown } }).payload;
    const err = payload?.error;
    if (typeof err === 'string') return err;
    if (err && typeof err === 'object' && 'message' in err) return String((err as { message: unknown }).message);
    if (e instanceof Error && e.message) return e.message;
  }
  return fallback;
}

// ─────────────────────────────────────────────────────────────────────────────
// ModalGeneraDdt — crea un DDT (BOZZA) selezionando le righe consegnabili.
// VENDITA: candidati da GET /api/ddt/sorgente-ordine?ordineId=
// CANTIERE: candidati da GET /api/ddt/sorgente-distinta?distintaId=
// I dati di trasporto/destinazione si completano poi nella scheda DDT (bozza).
// ─────────────────────────────────────────────────────────────────────────────
export default function ModalGeneraDdt({ tipo, ordineId, distintaId, onClose, onCreated }: Props) {
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [candidati, setCandidati] = useState<CandidatoDdt[]>([]);
  const [selezionati, setSelezionati] = useState<Map<string, string>>(new Map());

  const chiave = (c: CandidatoDdt) =>
    c.rigaDistintaId ? `D-${c.rigaDistintaId}` : c.componenteId ? `C-${c.componenteId}` : `R-${c.rigaOrdineId}`;

  useEffect(() => {
    let attivo = true;
    (async () => {
      setLoading(true);
      try {
        let res: { success: boolean; data?: { candidati: CandidatoDdt[] }; error?: unknown };
        if (tipo === 'VENDITA') {
          // @api GET /api/ddt/sorgente-ordine?ordineId -> {"success":true,"data":{"ordine":{"id":"number","numero":"string"},"candidati":[{"rigaOrdineId":"number|null","componenteId":"number|null","rigaDistintaId":"number|null","articoloId":"number|null","codice":"string|null","descrizione":"string","unitaMisura":"string|null","quantita":"number|null","giaConsegnata":"number","residuo":"number|null"}]}}
          res = await apiFetch(`/api/ddt/sorgente-ordine?ordineId=${ordineId}`);
        } else {
          // @api GET /api/ddt/sorgente-distinta?distintaId -> {"success":true,"data":{"distinta":{"id":"number","etichetta":"string"},"candidati":[{"rigaOrdineId":"number|null","componenteId":"number|null","rigaDistintaId":"number","articoloId":"number|null","codice":"string|null","descrizione":"string","unitaMisura":"string|null","quantita":"number","giaConsegnata":"number","residuo":"number"}]}}
          res = await apiFetch(`/api/ddt/sorgente-distinta?distintaId=${distintaId}`);
        }
        if (!attivo) return;
        if (!res.success) {
          showToast(estraiErrore({ payload: res }, 'Errore nel caricamento delle righe consegnabili'), 'error');
          setCandidati([]);
        } else {
          const cands = res.data?.candidati ?? [];
          setCandidati(cands);
          // Preseleziona tutte le righe con residuo > 0
          const pre = new Map<string, string>();
          for (const c of cands) {
            if (c.residuo != null && c.residuo > 0) {
              pre.set(
                c.rigaDistintaId ? `D-${c.rigaDistintaId}` : c.componenteId ? `C-${c.componenteId}` : `R-${c.rigaOrdineId}`,
                String(c.residuo)
              );
            }
          }
          setSelezionati(pre);
        }
      } catch (e) {
        if (attivo) {
          showToast(estraiErrore(e, 'Errore nel caricamento'), 'error');
          setCandidati([]);
        }
      } finally {
        if (attivo) setLoading(false);
      }
    })();
    return () => { attivo = false; };
  }, [tipo, ordineId, distintaId]);

  const toggle = (c: CandidatoDdt) => {
    setSelezionati((prev) => {
      const next = new Map(prev);
      const k = chiave(c);
      if (next.has(k)) next.delete(k);
      else next.set(k, String(c.residuo != null && c.residuo > 0 ? c.residuo : (c.quantita ?? 1)));
      return next;
    });
  };

  const setQuantita = (c: CandidatoDdt, val: string) => {
    setSelezionati((prev) => {
      const next = new Map(prev);
      next.set(chiave(c), val);
      return next;
    });
  };

  const conferma = async () => {
    const righe: Array<{
      rigaOrdineId: number | null; componenteId: number | null; rigaDistintaId: number | null;
      articoloId: number | null; descrizione: string; umVendita: string | null; quantita: number;
    }> = [];
    for (const c of candidati) {
      const k = chiave(c);
      if (!selezionati.has(k)) continue;
      const q = Number(String(selezionati.get(k)).replace(',', '.'));
      if (!Number.isFinite(q) || q <= 0) {
        showToast(`Quantità non valida per "${c.descrizione}"`, 'error');
        return;
      }
      righe.push({
        rigaOrdineId: c.rigaOrdineId,
        componenteId: c.componenteId,
        rigaDistintaId: c.rigaDistintaId,
        articoloId: c.articoloId,
        descrizione: c.descrizione,
        umVendita: c.unitaMisura,
        quantita: q,
      });
    }
    if (righe.length === 0) {
      showToast('Seleziona almeno una riga da consegnare', 'error');
      return;
    }
    setSalvando(true);
    try {
      // @api POST /api/ddt {"tipo":"VENDITA|CANTIERE","ordineId":"number?","distintaId":"number?","righe":"array"} -> {"success":true,"data":{"id":"number"}}
      const res = await apiFetch<{ success: boolean; data?: { id: number }; error?: unknown }>('/api/ddt', {
        method: 'POST',
        body: JSON.stringify(
          tipo === 'VENDITA'
            ? { tipo, ordineId, righe }
            : { tipo, distintaId, righe }
        ),
      });
      if (!res.success || !res.data) {
        showToast(estraiErrore({ payload: res }, 'Errore nella creazione del DDT'), 'error');
      } else {
        showToast('DDT creato in bozza', 'success');
        onCreated(res.data.id);
      }
    } catch (e) {
      showToast(estraiErrore(e, 'Errore nella creazione del DDT'), 'error');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="bop-modal-overlay" onClick={onClose}>
      <div className="bop-modal bop-modal--xl" onClick={(e) => e.stopPropagation()}>
        <div className="bop-modal__header">
          <h2 className="bop-modal__title">
            {tipo === 'VENDITA' ? 'Genera DDT di vendita' : 'Genera DDT su cantiere'}
          </h2>
          <button type="button" className="bop-modal__close" onClick={onClose} aria-label="Chiudi">×</button>
        </div>
        <div className="bop-modal__body">
          {loading ? (
            <div className="bop-empty-state">Caricamento righe consegnabili…</div>
          ) : candidati.length === 0 ? (
            <div className="bop-empty-state">Nessuna riga merce consegnabile.</div>
          ) : (
            <table className="bop-table">
              <thead>
                <tr>
                  <th className="bop-table__th--select"></th>
                  <th>Codice</th>
                  <th>Descrizione</th>
                  <th>U.M.</th>
                  <th className="bop-text-right">Q.tà</th>
                  <th className="bop-text-right">Già consegnata</th>
                  <th className="bop-text-right">Residuo</th>
                  <th className="bop-text-right">Q.tà da consegnare</th>
                </tr>
              </thead>
              <tbody>
                {candidati.map((c) => {
                  const k = chiave(c);
                  const sel = selezionati.has(k);
                  return (
                    <tr key={k}>
                      <td className="bop-table__td--select">
                        <input type="checkbox" checked={sel} onChange={() => toggle(c)} />
                      </td>
                      <td>{c.codice ?? <span className="bop-text-muted">—</span>}</td>
                      <td>{c.descrizione}</td>
                      <td>{c.unitaMisura ?? '—'}</td>
                      <td className="bop-text-right">{c.quantita != null ? formatQuantity(c.quantita) : '—'}</td>
                      <td className="bop-text-right">{formatQuantity(c.giaConsegnata)}</td>
                      <td className="bop-text-right">
                        {c.residuo != null
                          ? (c.residuo > 0 ? formatQuantity(c.residuo) : <span className="bop-badge bop-badge-success">Consegnato</span>)
                          : '—'}
                      </td>
                      <td className="bop-text-right">
                        <input
                          type="text"
                          className="bop-input bop-estrai__qta-input"
                          value={sel ? selezionati.get(k) ?? '' : ''}
                          disabled={!sel}
                          onChange={(e) => setQuantita(c, e.target.value)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="bop-modal__footer">
          <button type="button" className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
          <button type="button" className="bop-btn bop-btn-primary" disabled={loading || salvando} onClick={conferma}>
            {salvando ? 'Creazione…' : 'Crea DDT in bozza'}
          </button>
        </div>
      </div>
    </div>
  );
}
